import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Trash2, Search, UserPlus, Coins } from "lucide-react";
import { Toaster, toast } from "sonner";
import FirstTimeMatchWizard from "../components/relationships/FirstTimeMatchWizard";
import { useCoinGate } from "../contexts/CoinGateContext";
import CoachSidebar from "../components/eroscoach/CoachSidebar";
import CoachHeader from "../components/eroscoach/CoachHeader";
import CoachChatPane from "../components/eroscoach/CoachChatPane";

/**
 * ErosCoach - Isolated V1 Advice Mode Chat
 *
 * Single LLM call, no V2 orchestration.
 * Uses dedicated backend: /api/eroscoach/chat
 * Stores in: coach_messages, coach_sessions
 *
 * (P1 #2 refactor 2026-06-29) — page shell only. Sidebar / Header / Chat
 * Pane each extracted into `components/eroscoach/` modules. Prompt
 * library data moved to `data/promptLibraryData.js`.
 */
export default function ErosCoach() {
  const navigate = useNavigate();
  const location = useLocation();
  const { matchId } = useParams();
  const { user } = useAuth();

  // Seed selection state from the URL on FIRST render so the match
  // dropdown reflects the route immediately (avoids the "General Advice"
  // flash when returning from /plans?tab=topup with a match preserved).
  // `?session=<id>` in the URL (set by CoinBalanceLink when leaving to
  // /plans) also gets picked up on mount so the exact session hydrates.
  const _initialSearchParams = new URLSearchParams(window.location.search);
  const _initialSessionParam = _initialSearchParams.get("session") || null;

  // Chat state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(_initialSessionParam);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Match selection (for context)
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(matchId || null);
  // True when user has just switched match via dropdown and hasn't yet
  // explicitly picked a session (or hit "New session"). Suppresses
  // auto-load/auto-create and the wizard "should-show" check so they
  // don't fire with a stale currentSessionId from the previous match.
  const [pendingMatchSelection, setPendingMatchSelection] = useState(false);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const creatingSessionRef = useRef(false);
  // Tracks the session_id of the most recently CREATED (not loaded) session,
  // so the wizard effect can distinguish "+ new session" from "user clicked
  // an existing session in the sidebar". Only freshly created sessions are
  // eligible for the wizard popup OR the silent auto-greet path.
  const justCreatedSessionIdRef = useRef(null);

  // User persona for avatar
  const [userPersona, setUserPersona] = useState(null);

  // Sidebar state - use sidebarOpen like /plog instead of collapsed
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Memory Plan v1 — Phase 3 perspective sheet
  const [perspectiveOpen, setPerspectiveOpen] = useState(false);
  // Bumped each time the Sheet transitions to open so PerspectivePanel
  // re-fetches fresh data (digest re-runs would otherwise show stale state).
  const [perspectiveRefreshKey, setPerspectiveRefreshKey] = useState(0);
  // Red-dot indicator on the Brain icon — lights up when either explore
  // button (gap-summary CTA or asynchronicity banner) is currently surfacing
  // a coaching disconnect for the selected match. Cleared when the user
  // actually sends an exploration prompt (the `eros:inject-prompt` listener
  // below flips `explorationAcknowledged`). Re-evaluated on every match
  // change.
  const [hasPendingExploration, setHasPendingExploration] = useState(false);
  const [explorationAcknowledged, setExplorationAcknowledged] = useState(false);
  // Blue-dot indicator on the Brain icon — lights up when there is at least
  // one pending bio_claim_action OR narrative_synthesis_action for the
  // currently selected match (either side — match_persona or user_persona).
  // Sums totals from two endpoints scoped to the selected match. Cleared
  // when the count drops to zero on next match change OR perspective refresh.
  const [pendingBioUpdatesCount, setPendingBioUpdatesCount] = useState(0);
  // Which Brain Sheet tab to land on when the sheet opens. Default "claims".
  // When the user clicks the Brain icon while a red-dot exploration is
  // pending we deep-link to "stage" so the gap/asynchronicity surfacing the
  // dot is immediately visible.
  const [brainTab, setBrainTab] = useState("claims");

  // Filter state
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Search state - persisted search results accessible from dropdown
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScope, setSearchScope] = useState("all"); // "session", "relationship", "all"
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [savedSearchQuery, setSavedSearchQuery] = useState(""); // Remember what was searched

  // Assign to relationship modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningSession, setAssigningSession] = useState(null);
  const [assignMatchId, setAssignMatchId] = useState("");

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  // Coin balance state
  const [coinBalance, setCoinBalance] = useState(null);
  const { requireCoins, openOutOfCoinsModal } = useCoinGate();
  // Legacy shim for children still passing setShowCoinPurchaseModal as a
  // prop — routed to the global CoinGate modal now that the per-page
  // dialog has been lifted out of this file.
  const setShowCoinPurchaseModal = (value) => { if (value) openOutOfCoinsModal(); };

  // Chat input ref for inserting prompts
  const chatInputRef = useRef(null);

  // Prompt Library state
  const [promptLibraryStatus, setPromptLibraryStatus] = useState("love_interest");

  // Get the current match object for prompt library
  const currentMatchObject = matches.find(m => m.match_id === selectedMatch);

  // Handle prompt selection
  const handlePromptSelect = (prompt) => {
    const matchName = currentMatchObject?.name || "them";
    const filledPrompt = prompt.replace(/{match_name}/g, matchName).replace(/{match_persona}/g, matchName);
    if (chatInputRef.current) {
      chatInputRef.current.setValue(filledPrompt);
    }
    setSettingsOpen(false);
  };

  // Cross-component bridge: StageSyncBar's "Explore this gap" chip dispatches
  // a `eros:inject-prompt` window event with `{ text }`. We inject it into the
  // chat input (reusing the same ref/setter the prompt library uses) and
  // collapse the Brain Sheet so the user is back to the conversation full-screen
  // with the question ready to send or edit.
  useEffect(() => {
    const onInject = (e) => {
      const text = e?.detail?.text;
      if (!text) return;
      if (chatInputRef.current) {
        chatInputRef.current.setValue(text);
      }
      setPerspectiveOpen(false);
      // Clear the red-dot — the user has acknowledged the disconnect by
      // injecting a calibration prompt. The dot will only re-light if a
      // future digest land surfaces a fresh gap (next match-change refresh).
      setExplorationAcknowledged(true);
      setHasPendingExploration(false);
    };
    window.addEventListener("eros:inject-prompt", onInject);
    return () => window.removeEventListener("eros:inject-prompt", onInject);
  }, []);

  // Red-dot pre-check: whenever the selected match changes, fetch the
  // stage-sync snapshot and decide whether to surface the indicator on the
  // Brain icon. Two triggers count as a "pending exploration":
  //   (a) assumed-vs-inferred-user gap (the gap-summary CTA would render)
  //   (b) inferred-user-vs-inferred-match gap ≥ 2 (the asynchronicity banner
  //       would render)
  // Both signals come straight off /stage-sync — no extra round-trip needed.
  //
  // Also runs on the same 20s poll cadence as the blue-dot fetcher so that
  // a coaching gap emerging mid-session (after the digest worker re-scores
  // the stage trajectory) lights up the dot without requiring a match
  // re-select. The user's acknowledgement flag is reset ONLY on dep-change
  // (match change) — interval ticks must not clear acknowledgement, or the
  // dot would re-light every 20s after the user injected a calibration
  // prompt.
  useEffect(() => {
    if (!selectedMatch) {
      setHasPendingExploration(false);
      setExplorationAcknowledged(false);
      return undefined;
    }
    let cancelled = false;
    // Reset acknowledgement on match change only — NOT on each poll tick.
    setExplorationAcknowledged(false);
    const fetchExploration = async () => {
      try {
        const res = await authAxios.get(
          `/relationship/${selectedMatch}/stage-sync`,
          {
            params: { _: Date.now() },
            headers: { "Cache-Control": "no-store" },
          }
        );
        if (cancelled) return;
        const d = res.data || {};
        const assumed = d.persona_connection;
        const userVal = d.inferred_user_stage?.value;
        const matchVal = d.inferred_match_stage?.value;
        const gapSummaryActive =
          !d.digest_authoritative &&
          typeof assumed === "number" &&
          typeof userVal === "number" &&
          assumed !== userVal;
        const asyncBannerActive =
          typeof userVal === "number" &&
          typeof matchVal === "number" &&
          Math.abs(userVal - matchVal) >= 2;
        setHasPendingExploration(gapSummaryActive || asyncBannerActive);
      } catch {
        if (!cancelled) setHasPendingExploration(false);
      }
    };
    fetchExploration();
    const intervalId = setInterval(fetchExploration, 20000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedMatch]);

  // Blue-dot pre-check: whenever the selected match (or its user_persona)
  // changes, OR the perspective sheet just refreshed, fetch the pending
  // bio_claim_actions + narrative_synthesis_actions counts and sum them.
  // Also runs on a 20-second poll while the page is mounted so that
  // background-emitted bio updates from in-session digests light up the
  // dot WITHOUT requiring the user to open the perspective panel first
  // (the perspective-open trigger is now a complement, not a precondition).
  // Drives the blue flashing dot on the Brain icon — signals to the user
  // "Eros has new bio updates waiting for your review in
  // /relationships/<this match>/Updates".
  useEffect(() => {
    if (!selectedMatch) {
      setPendingBioUpdatesCount(0);
      return undefined;
    }
    let cancelled = false;
    const upId = userPersona?.match_id;
    const fetchCount = async () => {
      try {
        const requests = [
          authAxios.get("/bio-claims", {
            params: {
              status: "pending",
              target_entity_id: selectedMatch,
              page_size: 1,
            },
          }),
          authAxios.get("/bio-claims/narrative", {
            params: {
              status: "pending",
              target_entity_id: selectedMatch,
              page_size: 1,
            },
          }),
        ];
        // User-persona side (only the bio_claim channel; narrative synthesis
        // is match-persona-only in Phase 2).
        if (upId) {
          requests.push(
            authAxios.get("/bio-claims", {
              params: {
                status: "pending",
                target_entity_id: upId,
                page_size: 1,
              },
            })
          );
        }
        const responses = await Promise.all(requests);
        if (cancelled) return;
        const total = responses.reduce(
          (sum, r) => sum + (Number(r?.data?.total) || 0), 0,
        );
        setPendingBioUpdatesCount(total);
      } catch {
        if (!cancelled) setPendingBioUpdatesCount(0);
      }
    };
    // Immediate fetch on mount / dep change.
    fetchCount();
    // Lightweight 20s poll — backend digest runs are async to the chat
    // turn; this surfaces newly-created pending rows shortly after the
    // digest worker lands them, without needing the user to navigate.
    const intervalId = setInterval(fetchCount, 20000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedMatch, userPersona?.match_id, perspectiveRefreshKey]);

  // Update promptLibraryStatus when match changes
  useEffect(() => {
    if (currentMatchObject?.relationship_status) {
      setPromptLibraryStatus(currentMatchObject.relationship_status);
    } else {
      setPromptLibraryStatus("love_interest");
    }
  }, [currentMatchObject]);

  // Set selectedMatch from URL param when matches are loaded
  useEffect(() => {
    if (matchId && matches.length > 0) {
      const matchExists = matches.some(m => m.match_id === matchId);
      if (matchExists) {
        setSelectedMatch(matchId);
      }
    }
  }, [matchId, matches]);

  // Filter sessions based on selected match and favorites
  const filteredSessions = isSearchActive
    ? searchResults
    : sessions.filter(session => {
        // First filter by match
        let matchFilter;
        if (selectedMatch) {
          matchFilter = session.match_id === selectedMatch;
        } else {
          matchFilter = !session.match_id;
        }

        // Then filter by favorites if enabled
        if (showFavoritesOnly) {
          return matchFilter && session.is_favourite;
        }
        return matchFilter;
      });

  // Handle match selection changes - auto-create session if needed
  useEffect(() => {
    if (isSearchActive) return;
    if (sessionsLoading || !matchesLoaded) return; // Wait for BOTH to finish

    // After a dropdown-initiated match change, the user must explicitly
    // pick a session (or click "New session") via the empty-state UI.
    // Skip auto-load/auto-create so we don't carry stale state forward.
    if (pendingMatchSelection) return;

    // BUG FIX — race-condition guard:
    // When the user lands on /eroscoach/:matchId, the URL→state mirror
    // (setSelectedMatch(matchId)) is queued but doesn't apply until the
    // NEXT render. If we let this effect run with the stale null
    // `selectedMatch`, it auto-creates a session with `match_id: null`
    // (direct-chat bucket) BEFORE the mirror catches up. The user then
    // chats into what looks like the right session but it has no match
    // anchor, so the digest worker correctly skips it.
    //
    // Hold off until the mirror has settled. If the URL matchId is
    // bogus (no matching record), fall through and let direct-chat
    // creation proceed.
    if (matchId && selectedMatch !== matchId) {
      const exists = matches.some(m => m.match_id === matchId);
      if (exists) return;
      // else: URL matchId is invalid — fall through to direct-chat.
    }

    const matchFilteredSessions = sessions.filter(s =>
      selectedMatch ? s.match_id === selectedMatch : !s.match_id
    );

    // If the URL asked for a specific session (?session=…) via CoinBalanceLink,
    // honour it as long as it exists for this match. Otherwise pick the newest.
    const urlSessionExists =
      currentSessionId &&
      matchFilteredSessions.some(s => s.session_id === currentSessionId);

    if (matchFilteredSessions.length === 0) {
      createNewSessionForMatch(selectedMatch);
    } else if (urlSessionExists) {
      // The URL's requested session exists — hydrate its messages if we
      // haven't already loaded them.
      if (messages.length === 0) {
        loadSession(currentSessionId);
      }
    } else {
      const latestSession = matchFilteredSessions[0];
      if (currentSessionId !== latestSession.session_id) {
        loadSession(latestSession.session_id);
      }
    }
  }, [selectedMatch, sessions, sessionsLoading, matchesLoaded, isSearchActive, matchId, matches, pendingMatchSelection]);

  // ─── URL ↔ state sync (URL is the source of truth) ─────────────────
  // ONE atomic mirror effect keeps the address bar reflecting the
  // currently active match + session. Consolidated from two separate
  // effects because React batches state updates: when the CoachHeader
  // dropdown fires setSelectedMatch + setCurrentSessionId in the same
  // handler, two effects each read a stale `location` from the same
  // render closure — the second navigate() would overwrite the first
  // with a stale pathname, leaving the URL frozen. Using window.location
  // as the fresh reference sidesteps the closure-capture race entirely.
  // `replace: true` avoids polluting browser history per dropdown change.
  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    const desiredPath = selectedMatch ? `/eroscoach/${selectedMatch}` : `/eroscoach`;
    const params = new URLSearchParams(currentSearch);
    if (currentSessionId) params.set("session", currentSessionId);
    else params.delete("session");
    const qs = params.toString();
    const desiredUrl = `${desiredPath}${qs ? `?${qs}` : ""}${currentHash}`;
    const currentUrl = `${currentPath}${currentSearch}${currentHash}`;
    if (desiredUrl !== currentUrl) {
      navigate(desiredUrl, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatch, currentSessionId]);

  // ─── "Resume last chat" via localStorage ───────────────────────────
  // Persist the active match+session so a bare visit to /eroscoach
  // resumes the user's most recent context. User-scoped so a signout
  // + different signin on a shared device does NOT leak state.
  const _lastContextKey = user?.user_id ? `agent_eros_coach_last_context_${user.user_id}` : null;

  // Write on every match/session change (only when both matches are loaded
  // and we're not in a mid-hydration state).
  useEffect(() => {
    if (!_lastContextKey) return;
    try {
      const payload = {
        matchId: selectedMatch || null,
        sessionId: currentSessionId || null,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(_lastContextKey, JSON.stringify(payload));
    } catch { /* private mode / quota */ }
  }, [selectedMatch, currentSessionId, _lastContextKey]);

  // Hydrate on mount — ONLY when the URL has no matchId AND no ?session=
  // AND matches are loaded (so we can validate the stored matchId is real).
  const _didHydrateRef = useRef(false);
  useEffect(() => {
    if (_didHydrateRef.current) return;
    if (!_lastContextKey || !matchesLoaded) return;
    // URL wins — don't override deep-links or in-flight ?session= params.
    if (matchId || _initialSessionParam) { _didHydrateRef.current = true; return; }
    try {
      const raw = localStorage.getItem(_lastContextKey);
      if (!raw) { _didHydrateRef.current = true; return; }
      const saved = JSON.parse(raw);
      // Validate the saved matchId still exists for this user; if not,
      // silently discard (deleted relationships shouldn't strand users).
      const stillExists = saved.matchId
        ? matches.some((m) => m.match_id === saved.matchId)
        : true;
      if (stillExists && saved.matchId) {
        setSelectedMatch(saved.matchId);
        if (saved.sessionId) setCurrentSessionId(saved.sessionId);
      } else if (!stillExists) {
        localStorage.removeItem(_lastContextKey);
      }
    } catch { /* corrupt payload — ignore */ }
    _didHydrateRef.current = true;
  }, [_lastContextKey, matchesLoaded, matchId, _initialSessionParam, matches]);

  // Convenience: navigate to /plans?tab=topup preserving the current
  // (now-authoritative) URL so the plans back-arrow returns to the
  // exact match + session. Passed to CoachChatPane so the in-chat
  // "Purchase Coins" button lands on /plans instead of the embedded modal.
  const goToTopupPage = () => {
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate("/plans?tab=topup", { state: { from } });
  };

  // Create new session for specific match (without toast, with concurrency guard)
  const createNewSessionForMatch = async (matchId) => {
    if (creatingSessionRef.current) return; // Prevent concurrent creation
    if (!(await requireCoins())) return;
    creatingSessionRef.current = true;
    try {
      const response = await authAxios.post("/eroscoach/sessions", {
        match_id: matchId || null
      });

      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.session_id);
      setMessages([]);
      justCreatedSessionIdRef.current = newSession.session_id;
    } catch (error) {
      console.error("Failed to create session:", error);
      toast.error("Failed to create session");
    } finally {
      creatingSessionRef.current = false;
    }
  };

  // Settings
  const [maxWords] = useState(150);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // First-Time Match Wizard
  const [wizardState, setWizardState] = useState({
    open: false,
    matchId: null,
    matchName: "",
    userPersonaName: "",
    sessionId: null,
  });
  const wizardCheckedRef = useRef(new Set()); // dedupe per (sid|mid) pair

  useEffect(() => {
    if (!currentSessionId || !selectedMatch) return;
    // While the user is in the "pick a session" empty state, currentSessionId
    // may refer to a previous match's session. Don't fire the wizard
    // should-show check until the user has confirmed a session.
    if (pendingMatchSelection) return;
    // Only fire on a session that was JUST CREATED in this client (either via
    // the explicit "+" button or the auto-create-on-match-entry path). Loading
    // an existing session from the sidebar must NOT trigger the greeting.
    if (justCreatedSessionIdRef.current !== currentSessionId) return;
    const key = `${currentSessionId}|${selectedMatch}`;
    if (wizardCheckedRef.current.has(key)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get(
          `/relationship/${selectedMatch}/wizard/should-show`
        );
        if (cancelled) return;
        // Only mark the key as "checked" AFTER we got an answer — prevents
        // a poisoned cache when matches[] hasn't loaded yet on first render.
        wizardCheckedRef.current.add(key);
        // Consume the "just created" marker so re-renders don't re-trigger.
        justCreatedSessionIdRef.current = null;
        if (res.data?.show) {
          // Path A: popup-gated (zero active claims). User commits or skips,
          // then the popup itself POSTs /wizard-greet with the chosen mode.
          const matchDoc = matches.find((m) => m.match_id === selectedMatch);
          setWizardState({
            open: true,
            matchId: selectedMatch,
            matchName: res.data.match_name || matchDoc?.name || "this person",
            userPersonaName: res.data.user_persona_name || "",
            sessionId: currentSessionId,
          });
        } else {
          // Path B: silent auto-greet (active claims exist → returning user).
          // Fire /wizard-greet with mode="skipped"; backend routes to either
          // wizard_first_greeting (no prior user msgs for this match) or
          // wizard_intro_greeting ("Welcome back") based on prior msg count.
          // On LLM failure the backend persists a templated fallback greeting,
          // so loadSession will still surface a message.
          try {
            await authAxios.post(
              `/relationship/${selectedMatch}/wizard-greet`,
              { session_id: currentSessionId, mode: "skipped" }
            );
            if (cancelled) return;
            await loadSession(currentSessionId);
          } catch (e) {
            console.warn("Silent wizard auto-greet failed:", e);
          }
        }
      } catch (e) {
        // On should-show failure: don't poison the cache; allow retry on next
        // effect run.
        console.warn("Wizard should-show check failed:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentSessionId, selectedMatch, matches]);

  function closeWizard(result) {
    setWizardState((s) => ({ ...s, open: false }));
    // Reload session messages so the greeting appears in chat
    if (result?.committed && wizardState.sessionId) {
      loadSession(wizardState.sessionId);
    }
  }

  // Edit title modal state
  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState("");

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load sessions and matches on mount
  useEffect(() => {
    fetchSessions();
    fetchMatches();
    fetchUserPersona();
    fetchCoinBalance();
  }, []);

  // Fetch coin balance
  const fetchCoinBalance = async () => {
    try {
      const response = await authAxios.get("/credits/balance");
      setCoinBalance(response.data.balance);
    } catch (error) {
      console.error("Failed to fetch coin balance:", error);
    }
  };

  // Fetch coach sessions
  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const response = await authAxios.get("/eroscoach/sessions");
      setSessions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      toast.error("Failed to load sessions");
    } finally {
      setSessionsLoading(false);
    }
  };

  // Fetch user persona for avatar
  const fetchUserPersona = async () => {
    try {
      const response = await authAxios.get("/matches");
      const persona = response.data?.find(m => m.match_type === "user_persona");
      if (persona) {
        setUserPersona(persona);
      }
    } catch (error) {
      console.error("Failed to fetch user persona:", error);
    }
  };

  // Open edit title modal
  const openEditTitle = (session, e) => {
    e?.stopPropagation();
    setEditingSession(session);
    setEditTitleValue(session.title);
    setEditTitleOpen(true);
  };

  // Update session title
  const updateSessionTitle = async () => {
    if (!editingSession || !editTitleValue.trim()) return;

    try {
      await authAxios.patch(`/eroscoach/sessions/${editingSession.session_id}/title`, {
        title: editTitleValue.trim()
      });

      setSessions(prev => prev.map(s =>
        s.session_id === editingSession.session_id
          ? { ...s, title: editTitleValue.trim() }
          : s
      ));

      setEditTitleOpen(false);
      setEditingSession(null);
      toast.success("Title updated");
    } catch (error) {
      console.error("Failed to update title:", error);
      toast.error("Failed to update title");
    }
  };

  // Toggle favorite
  const toggleFavorite = async (session, e) => {
    e?.stopPropagation();

    try {
      const newValue = !session.is_favourite;
      await authAxios.patch(`/eroscoach/sessions/${session.session_id}/favourite`, {
        is_favourite: newValue
      });

      setSessions(prev => prev.map(s =>
        s.session_id === session.session_id
          ? { ...s, is_favourite: newValue }
          : s
      ));

      toast.success(newValue ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      toast.error("Failed to update favorite");
    }
  };

  // Open assign modal
  const openAssignModal = (session, e) => {
    e?.stopPropagation();
    setAssigningSession(session);
    setAssignMatchId(session.match_id || "");
    setAssignModalOpen(true);
  };

  // Assign session to relationship
  const assignToRelationship = async () => {
    if (!assigningSession) return;

    try {
      await authAxios.patch(`/eroscoach/sessions/${assigningSession.session_id}/assign`, {
        match_id: assignMatchId || null
      });

      setSessions(prev => prev.map(s =>
        s.session_id === assigningSession.session_id
          ? { ...s, match_id: assignMatchId || null, match_name: matches.find(m => m.match_id === assignMatchId)?.name || null }
          : s
      ));

      setAssignModalOpen(false);
      setAssigningSession(null);
      toast.success("Session assigned");
    } catch (error) {
      console.error("Failed to assign session:", error);
      toast.error("Failed to assign session");
    }
  };

  // Search sessions
  const performSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearchLoading(true);
      const params = new URLSearchParams({
        q: searchQuery,
        scope: searchScope,
      });

      if (searchScope === "session" && currentSessionId) {
        params.append("session_id", currentSessionId);
      } else if (searchScope === "relationship" && selectedMatch) {
        params.append("match_id", selectedMatch);
      }

      const response = await authAxios.get(`/eroscoach/sessions/search?${params}`);
      setSearchResults(response.data || []);
      setSavedSearchQuery(searchQuery); // Save the query for display
      setIsSearchActive(true);
      setSearchModalOpen(false);
      toast.success(`Found ${response.data?.length || 0} results`);
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search - but keep results accessible
  const clearSearch = () => {
    setIsSearchActive(false);
    // Don't clear searchResults or savedSearchQuery - keep them for dropdown access
  };

  // Fully clear search results
  const fullyClearSearch = () => {
    setIsSearchActive(false);
    setSearchResults([]);
    setSearchQuery("");
    setSavedSearchQuery("");
  };

  // Restore search results from dropdown
  const restoreSearchResults = () => {
    if (searchResults.length > 0) {
      setIsSearchActive(true);
    }
  };

  // Fetch matches for context selection
  const fetchMatches = async () => {
    try {
      const response = await authAxios.get("/matches");
      // Filter out user_persona from matches list
      const filteredMatches = (response.data || []).filter(m => m.match_type !== "user_persona");
      setMatches(filteredMatches);
    } catch (error) {
      console.error("Failed to fetch matches:", error);
    } finally {
      setMatchesLoaded(true);
    }
  };

  // Load session messages
  const loadSession = async (sessionId) => {
    try {
      setLoading(true);
      const response = await authAxios.get(`/eroscoach/sessions/${sessionId}/messages`);
      setMessages(response.data.messages || []);
      setCurrentSessionId(sessionId);
      setPendingMatchSelection(false);
    } catch (error) {
      console.error("Failed to load session:", error);
      toast.error("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  // Create new session
  const createNewSession = async () => {
    if (!(await requireCoins())) return;
    try {
      const response = await authAxios.post("/eroscoach/sessions", {
        match_id: selectedMatch || null
      });

      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.session_id);
      setMessages([]);
      setPendingMatchSelection(false);
      justCreatedSessionIdRef.current = newSession.session_id;
      toast.success("New conversation started");
    } catch (error) {
      console.error("Failed to create session:", error);
      toast.error("Failed to create session");
    }
  };

  // Confirm delete
  const confirmDelete = (sessionId, e) => {
    e?.stopPropagation();
    setDeletingSessionId(sessionId);
    setDeleteConfirmOpen(true);
  };

  // ─── New Session picker modal ─────────────────────────────────────────
  // Clicking the "+ New Session" button (desktop sidebar label or mobile
  // [NEW] button) opens this modal so the user must pick a relationship
  // BEFORE the session is created. This ensures selectedMatch is set at
  // creation time, which is what the perspective-seeder / wizard-greeter
  // effect keys off. Without this step, mobile users clicking [NEW] with
  // no match dropdown pre-selected would create a General Advice session
  // (match_id=null) and neither seeder nor wizard greeting would fire.
  const [newSessionModalOpen, setNewSessionModalOpen] = useState(false);
  const [newSessionMatchPick, setNewSessionMatchPick] = useState("none");
  const openNewSessionModal = async () => {
    if (!(await requireCoins())) return;
    setNewSessionMatchPick("none");
    setNewSessionModalOpen(true);
  };
  const handleConfirmNewSession = async () => {
    const matchId = newSessionMatchPick === "none" ? null : newSessionMatchPick;
    setNewSessionModalOpen(false);
    setMobileSidebarOpen(false);
    setSelectedMatch(matchId);
    setPendingMatchSelection(false);
    if (matchId) {
      // Match path — createNewSessionForMatch sets justCreatedSessionIdRef,
      // which is the trigger the wizard should-show / greeter effect keys off.
      await createNewSessionForMatch(matchId);
    } else {
      // General Advice — no match anchor, no wizard/seeder. Same code path
      // the old + button used before this modal existed.
      await createNewSession();
    }
    toast.success("New session started");
  };

  // Delete session
  const deleteSession = async () => {
    if (!deletingSessionId) return;

    try {
      await authAxios.delete(`/eroscoach/sessions/${deletingSessionId}`);
      setSessions(prev => prev.filter(s => s.session_id !== deletingSessionId));

      if (currentSessionId === deletingSessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }

      setDeleteConfirmOpen(false);
      setDeletingSessionId(null);
      toast.success("Session deleted");
    } catch (error) {
      console.error("Failed to delete session:", error);
      toast.error("Failed to delete session");
    }
  };

  // Admin-only: export a session to Markdown
  const isAdmin = user?.role === "admin";

  const formatUtc = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      // YYYY-MM-DD HH:MM:SS UTC — unambiguous + sortable
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
    } catch {
      return String(iso);
    }
  };

  const buildSessionMarkdown = ({ session, messages, userName, matchName, adminName }) => {
    const lines = [];
    lines.push(`# Session: ${session.title || "(untitled)"}`);
    lines.push("");
    lines.push("| Field | Value |");
    lines.push("|---|---|");
    lines.push(`| Session ID | \`${session.session_id}\` |`);
    lines.push(`| Created | ${formatUtc(session.created_at)} |`);
    lines.push(`| Match | ${matchName} |`);
    lines.push(`| Message count | ${messages.length} |`);
    lines.push(`| Exported by | ${adminName} on ${formatUtc(new Date().toISOString())} |`);
    lines.push("");
    lines.push("---");
    lines.push("");
    for (const m of messages) {
      const who = m.role === "assistant" ? "🤖 Agent Eros" : `🙋 ${userName}`;
      lines.push(`### ${who} — ${formatUtc(m.created_at)}`);
      lines.push("");
      lines.push((m.content || "").trim());
      lines.push("");
    }
    return lines.join("\n");
  };

  const downloadMarkdownFile = (markdown, filename) => {
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportSession = async (session, e) => {
    e?.stopPropagation();
    if (!isAdmin) return;
    try {
      const res = await authAxios.get(`/eroscoach/sessions/${session.session_id}/messages`);
      const messages = res.data?.messages || [];
      const matchName =
        matches.find((m) => m.match_id === session.match_id)?.name || "Unassigned";
      const userName =
        userPersona?.name || user?.name || user?.email || "User";
      const adminName = user?.name || user?.email || "Admin";
      const md = buildSessionMarkdown({
        session,
        messages,
        userName,
        matchName,
        adminName,
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      downloadMarkdownFile(md, `eros-session-${session.session_id}-${dateStr}.md`);
      toast.success("Session exported");
    } catch (err) {
      console.error("Failed to export session:", err);
      toast.error("Export failed");
    }
  };

  // Send message
  const sendMessage = async (message, options = {}) => {
    const { imageBase64, imageMimeType, imagePreview } = options;

    if (!message.trim() && !imageBase64) return;
    if (loading) return;

    const userMsg = message.trim();
    // Phase: in-chat retry — stamp a client-side id so we can target this
    // specific bubble for status updates (failed / needs_coins / retry).
    const clientId =
      (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
      `m_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    setLoading(true);

    // Add user message to chat with optional image
    const userMessage = {
      client_id: clientId,
      role: "user",
      content: userMsg,
      image: imagePreview ? { preview: imagePreview, mimeType: imageMimeType } : null,
      // Retry payload: kept until the message succeeds or is removed.
      _retryPayload: { message: userMsg, options },
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await authAxios.post("/eroscoach/chat", {
        message: userMsg,
        session_id: currentSessionId,
        match_id: selectedMatch,
        max_words: maxWords,
        image_base64: imageBase64,
        image_mime_type: imageMimeType
      }, {
        timeout: 120000
      });

      // Clear any prior failure marks on this bubble (defensive — covers
      // the implicit retry path where sendMessage is re-entered with a
      // fresh clientId).
      setMessages(prev => prev.map(m =>
        m.client_id === clientId
          ? { ...m, status: undefined, errorReason: undefined, coinBalanceAtFailure: undefined, _retryPayload: undefined }
          : m
      ));
      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response
      }]);

      if (response.data.session_id && response.data.session_id !== currentSessionId) {
        setCurrentSessionId(response.data.session_id);
      }

      // Refresh sidebar (title + message count) after every successful
      // send so the conversation list stays in sync without a manual
      // refresh action.
      fetchSessions();

      // Refresh coin balance after LLM call
      fetchCoinBalance();

    } catch (error) {
      console.error("Failed to send message:", error);

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || "";
      const rawBody = error?.response?.data;
      // K8s ingress returns plain-text "404 page not found" when the backend
      // pod is offline/sleeping. A real FastAPI 404 returns JSON
      // ({"detail": "Not Found"}), so this string is an unambiguous
      // backend-offline fingerprint and is safe to treat as transient.
      const isProxyOffline404 =
        status === 404 &&
        typeof rawBody === "string" &&
        rawBody.includes("404 page not found");
      const isImageTooLarge = detail.includes("image exceeds") || detail.includes("5 MB");
      const isTransient =
        !error.response ||
        [408, 429, 500, 502, 503, 504].includes(status) ||
        isProxyOffline404 ||
        detail.toLowerCase().includes("timeout");

      // 402 — insufficient coin balance. Per user choice "C":
      // keep message in chat with a "Purchase Coins" pill AND auto-open the
      // modal AND fire the toast. After purchase the pill flips to "retry".
      if (status === 402) {
        setMessages(prev => prev.map(m =>
          m.client_id === clientId
            ? {
                ...m,
                status: "needs_coins",
                errorReason: "needs_coins",
                coinBalanceAtFailure: coinBalance ?? 0,
              }
            : m
        ));
        toast.error("Insufficient coin balance");
        setShowCoinPurchaseModal(true);
        return;
      }

      // Image-too-large — retry will fail identically. Strip + toast.
      if (isImageTooLarge) {
        setMessages(prev => prev.filter(m => m.client_id !== clientId));
        toast.error("Image too large - please use an image under 5 MB");
        return;
      }

      // Transient — keep bubble in chat, marked failed, with a Retry pill.
      if (isTransient) {
        setMessages(prev => prev.map(m =>
          m.client_id === clientId
            ? { ...m, status: "failed", errorReason: "transient" }
            : m
        ));
        toast.error("Failed to send — tap the message to retry", { duration: 2500 });
        return;
      }

      // Fatal client error (400/403/404/etc.) — retry won't help. Strip + toast.
      let errorMsg = detail
        ? (detail.length > 100 ? detail.substring(0, 100) + "..." : detail)
        : "Failed to get response";
      setMessages(prev => prev.filter(m => m.client_id !== clientId));
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Retry a previously-failed user message. Removes the failed bubble and
  // re-runs sendMessage with the stored payload (incl. image base64).
  const retryMessage = (clientId) => {
    const failed = messages.find(m => m.client_id === clientId);
    if (!failed?._retryPayload) return;
    if (loading) return;
    setMessages(prev => prev.filter(m => m.client_id !== clientId));
    sendMessage(failed._retryPayload.message, failed._retryPayload.options || {});
  };

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster richColors position="top-center" />

      <CoachHeader
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
        navigate={navigate}
        matches={matches}
        selectedMatch={selectedMatch}
        setSelectedMatch={setSelectedMatch}
        setCurrentSessionId={setCurrentSessionId}
        setMessages={setMessages}
        setPendingMatchSelection={setPendingMatchSelection}
        isSearchActive={isSearchActive}
        searchResults={searchResults}
        restoreSearchResults={restoreSearchResults}
        clearSearch={clearSearch}
        coinBalance={coinBalance}
        setShowCoinPurchaseModal={setShowCoinPurchaseModal}
        perspectiveOpen={perspectiveOpen}
        setPerspectiveOpen={setPerspectiveOpen}
        perspectiveRefreshKey={perspectiveRefreshKey}
        setPerspectiveRefreshKey={setPerspectiveRefreshKey}
        hasPendingExploration={hasPendingExploration}
        explorationAcknowledged={explorationAcknowledged}
        pendingBioUpdatesCount={pendingBioUpdatesCount}
        brainTab={brainTab}
        setBrainTab={setBrainTab}
        currentMatchObject={currentMatchObject}
        currentSessionId={currentSessionId}
        userPersona={userPersona}
        settingsOpen={settingsOpen}
        setSettingsOpen={setSettingsOpen}
        promptLibraryStatus={promptLibraryStatus}
        setPromptLibraryStatus={setPromptLibraryStatus}
        handlePromptSelect={handlePromptSelect}
        createNewSession={createNewSession}
        fetchSessions={fetchSessions}
      />

      {/* Spacer for fixed header on mobile */}
      <div className="h-[57px] md:hidden" />

      <div className="flex h-[calc(100vh-57px)] md:h-[calc(100vh-65px)]">
        {/* Desktop Sidebar - Fixed height with internal scroll */}
        <div
          className={`${
            sidebarOpen ? 'w-72' : 'w-0'
          } hidden md:block transition-all duration-300 ease-in-out overflow-hidden border-r border-divine-gold/20 bg-divine-black-light/50 flex-shrink-0`}
        >
          <div className="w-72 h-full flex flex-col">
            <CoachSidebar
              isMobile={false}
              sessionsLoading={sessionsLoading}
              filteredSessions={filteredSessions}
              currentSessionId={currentSessionId}
              isSearchActive={isSearchActive}
              searchResults={searchResults}
              savedSearchQuery={savedSearchQuery}
              showFavoritesOnly={showFavoritesOnly}
              coinBalance={coinBalance}
              isAdmin={isAdmin}
              navigate={navigate}
              setMobileSidebarOpen={setMobileSidebarOpen}
              setSearchModalOpen={setSearchModalOpen}
              setShowCoinPurchaseModal={setShowCoinPurchaseModal}
              setShowFavoritesOnly={setShowFavoritesOnly}
              fullyClearSearch={fullyClearSearch}
              loadSession={loadSession}
              createNewSession={createNewSession}
              openNewSessionModal={openNewSessionModal}
              fetchSessions={fetchSessions}
              toggleFavorite={toggleFavorite}
              openEditTitle={openEditTitle}
              openAssignModal={openAssignModal}
              confirmDelete={confirmDelete}
              exportSession={exportSession}
            />
          </div>
        </div>

        {/* Mobile Sidebar - Sheet */}
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-divine-charcoal border-divine-gold/20">
            <CoachSidebar
              isMobile={true}
              sessionsLoading={sessionsLoading}
              filteredSessions={filteredSessions}
              currentSessionId={currentSessionId}
              isSearchActive={isSearchActive}
              searchResults={searchResults}
              savedSearchQuery={savedSearchQuery}
              showFavoritesOnly={showFavoritesOnly}
              coinBalance={coinBalance}
              isAdmin={isAdmin}
              navigate={navigate}
              setMobileSidebarOpen={setMobileSidebarOpen}
              setSearchModalOpen={setSearchModalOpen}
              setShowCoinPurchaseModal={setShowCoinPurchaseModal}
              setShowFavoritesOnly={setShowFavoritesOnly}
              fullyClearSearch={fullyClearSearch}
              loadSession={loadSession}
              createNewSession={createNewSession}
              openNewSessionModal={openNewSessionModal}
              fetchSessions={fetchSessions}
              toggleFavorite={toggleFavorite}
              openEditTitle={openEditTitle}
              openAssignModal={openAssignModal}
              confirmDelete={confirmDelete}
              exportSession={exportSession}
            />
          </SheetContent>
        </Sheet>

        <CoachChatPane
          chatContainerRef={chatContainerRef}
          messagesEndRef={messagesEndRef}
          chatInputRef={chatInputRef}
          messages={messages}
          loading={loading}
          pendingMatchSelection={pendingMatchSelection}
          selectedMatch={selectedMatch}
          sessions={sessions}
          matches={matches}
          coinBalance={coinBalance}
          setShowCoinPurchaseModal={setShowCoinPurchaseModal}
          goToTopupPage={goToTopupPage}
          loadSession={loadSession}
          createNewSession={createNewSession}
          sendMessage={sendMessage}
          retryMessage={retryMessage}
          currentSessionId={currentSessionId}
          userPersona={userPersona}
        />
      </div>

      {/* Rename Session Modal */}
      <Dialog open={editTitleOpen} onOpenChange={setEditTitleOpen}>
        <DialogContent className="backdrop-blur-none bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold">
              Rename Session
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              placeholder="Enter session title..."
              className="bg-white/5 border text-white border-divine-gold/20"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateSessionTitle();
                }
              }}
              data-testid="edit-title-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditTitleOpen(false)}
              className="text-white/60 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={updateSessionTitle}
              disabled={!editTitleValue.trim()}
              className="bg-divine-gold hover:bg-divine-gold-light text-black"
              data-testid="save-title-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Modal */}
      <Dialog open={searchModalOpen} onOpenChange={setSearchModalOpen}>
        <DialogContent className="backdrop-blur-none bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search Sessions
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for..."
              className="bg-white/5 border text-white border-divine-gold/20"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  performSearch();
                }
              }}
              data-testid="search-input"
            />

            <div>
              <label className="text-white/60 text-sm mb-2 block">Search Scope</label>
              <Select value={searchScope} onValueChange={setSearchScope}>
                <SelectTrigger className="bg-white/5 border-divine-gold/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="session" disabled={!currentSessionId}>This Session</SelectItem>
                  <SelectItem value="relationship" disabled={!selectedMatch}>This Relationship</SelectItem>
                  <SelectItem value="all">All Sessions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSearchModalOpen(false)}
              className="text-white/60 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={performSearch}
              disabled={!searchQuery.trim() || searchLoading}
              className="bg-divine-gold hover:bg-divine-gold-light text-black"
              data-testid="search-btn"
            >
              {searchLoading ? "Searching..." : "Search"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Relationship Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="backdrop-blur-none bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Assign to Relationship
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-white/60 text-sm mb-2 block">
              Assign this session to:
            </label>
            <Select value={assignMatchId || "none"} onValueChange={setAssignMatchId}>
              <SelectTrigger className="bg-white/5 border-divine-gold/20">
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General Advice (No relationship)</SelectItem>
                {matches.map(m => (
                  <SelectItem key={m.match_id} value={m.match_id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAssignModalOpen(false)}
              className="text-white/60 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={assignToRelationship}
              className="bg-divine-gold hover:bg-divine-gold-light text-black"
              data-testid="assign-btn"
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="backdrop-blur-none bg-divine-charcoal border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-red flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Session
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/80">
              Are you sure you want to delete this session? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteConfirmOpen(false)}
              className="text-white/60 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={deleteSession}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Out-of-Coins modal now lives globally in CoinGateProvider
          (frontend/src/contexts/CoinGateContext.jsx). setShowCoinPurchaseModal
          is a shim above that opens the global modal. */}

      {/* First-Time Match Wizard */}
      <FirstTimeMatchWizard
        open={wizardState.open}
        matchId={wizardState.matchId}
        matchName={wizardState.matchName}
        userPersonaName={wizardState.userPersonaName}
        sessionId={wizardState.sessionId}
        getSessionId={() => currentSessionId}
        onClose={closeWizard}
      />

      {/* New Session — Relationship Picker Modal (same look as /dashboard "Seek Advice") */}
      <Dialog open={newSessionModalOpen} onOpenChange={setNewSessionModalOpen}>
        <DialogContent className="bg-divine-black-light border-white/10 sm:max-w-md" data-testid="new-session-modal">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-medium">
              Select the relationship for your new session
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select
              value={newSessionMatchPick}
              onValueChange={setNewSessionMatchPick}
            >
              <SelectTrigger
                className="w-full bg-white/5 border-white/20 text-white"
                data-testid="new-session-match-selector"
              >
                <SelectValue placeholder="Select a relationship" />
              </SelectTrigger>
              <SelectContent className="bg-divine-black-light border-white/10">
                <SelectItem value="none" className="text-white/70">
                  None - General Advice
                </SelectItem>
                {matches
                  .filter((m) => m.match_type !== "user_persona")
                  .map((match) => (
                    <SelectItem
                      key={match.match_id}
                      value={match.match_id}
                      className="text-white"
                    >
                      {match.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleConfirmNewSession}
              className="bg-divine-gold hover:bg-divine-gold-light text-black font-medium"
              data-testid="start-new-session-btn"
            >
              Start Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
